/**
 * Plugin Storage
 *
 * Provides persistent storage for plugin-specific data.
 * Stores data in the database under plugin_settings.config JSONB field.
 *
 * Features:
 * - Scoped to plugin and organization
 * - Persistent across sessions
 * - Type-safe get/set operations
 * - Automatic serialization/deserialization
 */

import { PluginStorage as IPluginStorage } from '@/lib/platform/types/plugins';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/lib/platform/types/database.types';
import { createClient as createBrowserClient } from '@/lib/platform/supabase/client';

import { pluginLogger } from '@/lib/platform/utils/logger';

// Create Supabase client - uses service role on server, singleton in browser
function createSupabaseClient() {
  const isServer = typeof window === 'undefined';

  if (isServer && process.env.SUPABASE_SERVICE_ROLE_KEY) {
    // Server-side: use service role to bypass RLS for infrastructure operations
    return createClient<Database>(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY,
      {
        auth: { persistSession: false, autoRefreshToken: false },
      }
    );
  } else {
    // Client-side: use singleton browser client to avoid multiple GoTrueClient instances
    return createBrowserClient();
  }
}

const supabase = createSupabaseClient();

export class PluginStorage implements IPluginStorage {
  constructor(
    private pluginId: string,
    private organizationId: string
  ) {}

  /**
   * Get value from plugin storage
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const { data, error } = await supabase
        .from('plugin_settings')
        .select('config')
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId)
        .single();

      if (error || !data) {
        return null;
      }

      const config = (data.config || {}) as Record<string, unknown>;
      return config[key] !== undefined ? (config[key] as T) : null;
    } catch (error) {
      pluginLogger.error(`[PluginStorage] Error getting key "${key}":`, error);
      return null;
    }
  }

  /**
   * Set value in plugin storage
   */
  async set<T>(key: string, value: T): Promise<void> {
    try {
      // Get current config
      const { data: current } = await supabase
        .from('plugin_settings')
        .select('config')
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId)
        .single();

      const config = (current?.config || {}) as Record<string, unknown>;
      config[key] = value;

      // Update config
      const { error } = await supabase.from('plugin_settings').upsert({
        plugin_id: this.pluginId,
        organization_id: this.organizationId,
        config: config as import('@/lib/platform/types/database.types').Json,
        updated_at: new Date().toISOString(),
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      pluginLogger.error(`[PluginStorage] Error setting key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Delete value from plugin storage
   */
  async delete(key: string): Promise<void> {
    try {
      // Get current config
      const { data: current } = await supabase
        .from('plugin_settings')
        .select('config')
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId)
        .single();

      if (!current) {
        return;
      }

      const config = (current.config || {}) as Record<string, unknown>;
      delete config[key];

      // Update config
      const { error } = await supabase
        .from('plugin_settings')
        .update({
          config: config as import('@/lib/platform/types/database.types').Json,
          updated_at: new Date().toISOString(),
        })
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId);

      if (error) {
        throw error;
      }
    } catch (error) {
      pluginLogger.error(`[PluginStorage] Error deleting key "${key}":`, error);
      throw error;
    }
  }

  /**
   * Clear all plugin storage
   */
  async clear(): Promise<void> {
    try {
      const { error } = await supabase
        .from('plugin_settings')
        .update({
          config: {},
          updated_at: new Date().toISOString(),
        })
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId);

      if (error) {
        throw error;
      }
    } catch (error) {
      pluginLogger.error('[PluginStorage] Error clearing storage:', error);
      throw error;
    }
  }

  /**
   * Get all keys in storage
   */
  async keys(): Promise<string[]> {
    try {
      const { data } = await supabase
        .from('plugin_settings')
        .select('config')
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId)
        .single();

      if (!data || !data.config) {
        return [];
      }

      return Object.keys(data.config);
    } catch (error) {
      pluginLogger.error('[PluginStorage] Error getting keys:', error);
      return [];
    }
  }

  /**
   * Get entire config object
   */
  async getAll<T = Record<string, unknown>>(): Promise<T | null> {
    try {
      const { data } = await supabase
        .from('plugin_settings')
        .select('config')
        .eq('plugin_id', this.pluginId)
        .eq('organization_id', this.organizationId)
        .single();

      return (data?.config as T) || null;
    } catch (error) {
      pluginLogger.error('[PluginStorage] Error getting all config:', error);
      return null;
    }
  }

  /**
   * Set entire config object
   */
  async setAll<T = Record<string, unknown>>(config: T): Promise<void> {
    try {
      const { error } = await supabase.from('plugin_settings').upsert({
        plugin_id: this.pluginId,
        organization_id: this.organizationId,
        config: config as import('@/lib/platform/types/database.types').Json,
        updated_at: new Date().toISOString(),
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      pluginLogger.error('[PluginStorage] Error setting all config:', error);
      throw error;
    }
  }
}
